/**
 * Thin Cookiebot wrapper. Cookiebot is loaded in index.html (auto-blocking
 * mode) and exposes a global `Cookiebot` object plus DOM events when consent
 * changes. We gate our own first-party analytics behind the `statistics`
 * category so `dc_vid` and `crm-track` only fire after the user accepts.
 */

type ConsentState = {
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
};

type CookiebotGlobal = {
  consent?: Partial<ConsentState>;
  consented?: boolean;
  hasResponse?: boolean;
  renew?: () => void;
  show?: () => void;
  submitCustomConsent?: (preferences: boolean, statistics: boolean, marketing: boolean) => void;
};

declare global {
  interface Window {
    Cookiebot?: CookiebotGlobal;
  }
}

function readState(): ConsentState | null {
  if (typeof window === "undefined") return null;
  const cb = window.Cookiebot;
  if (!cb || !cb.consent) return null;
  if (cb.hasResponse === false) return null;
  return {
    necessary: !!cb.consent.necessary,
    preferences: !!cb.consent.preferences,
    statistics: !!cb.consent.statistics,
    marketing: !!cb.consent.marketing,
  };
}

export function getConsent(): ConsentState | null {
  return readState();
}

export function hasStatisticsConsent(): boolean {
  return !!readState()?.statistics;
}

type Listener = (s: ConsentState) => void;
const listeners = new Set<Listener>();

function emit() {
  const s = readState();
  if (!s) return;
  listeners.forEach((l) => {
    try {
      l(s);
    } catch {
      /* ignore */
    }
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("CookiebotOnAccept", emit);
  window.addEventListener("CookiebotOnDecline", emit);
  window.addEventListener("CookiebotOnConsentReady", emit);
}

/** Subscribe to consent changes. Fires immediately if consent is already known. */
export function onConsentChange(cb: Listener): () => void {
  listeners.add(cb);
  const current = readState();
  if (current) {
    try {
      cb(current);
    } catch {
      /* ignore */
    }
  }
  return () => {
    listeners.delete(cb);
  };
}

/** Open the Cookiebot dialog so the user can change their choice. */
export function renewConsent() {
  if (typeof window === "undefined") return;
  window.Cookiebot?.renew?.();
}

export function hasAnsweredConsent(): boolean {
  if (typeof window === "undefined") return true;
  return window.Cookiebot?.hasResponse === true;
}

export function acceptAllConsent() {
  if (typeof window === "undefined") return;
  window.Cookiebot?.submitCustomConsent?.(true, true, true);
}

export function denyOptionalConsent() {
  if (typeof window === "undefined") return;
  window.Cookiebot?.submitCustomConsent?.(false, false, false);
}
